/**
 * Review queue tab on the connection detail view: NeedsReview links on the
 * left, oldest match first; the selected case on the right with the Engage
 * values QIE compared against beside each EHR candidate, per-field evidence
 * markers, and Link / Reject actions. Shows PHI from the EHR; VITALL only.
 */
import type { ApolloError } from '@apollo/client';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useReviewEhrPatientLink } from '@hooks/api/ehr-sync-patients';
import { useSnackbar } from '@hooks/useSnackbar';

import type { EhrMatchField, EhrReviewCase } from '@models/ehr-sync';

import { formatClientId, formatDate } from '@utils/format';

import EvidenceMarker from '../../components/EvidenceMarker';
import RejectMatchDialog from '../../components/RejectMatchDialog';
import { patientName } from '../../components/patient-link.util';
import { commonMessages } from '../../messages';
import RelativeTime from '../shared/RelativeTime';
import StatePanel from '../shared/StatePanel';

const messages = defineMessages({
  notInQueue: {
    id: 'ehrSync.review.notInQueue',
    defaultMessage:
      'This patient is not in the review queue any more. It may have been decided already, or its match evidence could not be read. Pick a case from the list.',
  },
  queueTitle: {
    id: 'ehrSync.review.queueTitle',
    defaultMessage: 'Needs review',
  },
  oldestFirst: {
    id: 'ehrSync.review.oldestFirst',
    defaultMessage: 'Oldest first',
  },
  queueItemMeta: {
    id: 'ehrSync.review.queueItemMeta',
    defaultMessage:
      '{dob} · {count, plural, one {# candidate} other {# candidates}}',
  },
  waiting: { id: 'ehrSync.review.waiting', defaultMessage: 'Matched {when}' },
  dob: { id: 'ehrSync.review.dob', defaultMessage: 'DOB {date}' },
  noDob: { id: 'ehrSync.review.noDob', defaultMessage: 'No DOB' },
  empty: {
    id: 'ehrSync.review.empty',
    defaultMessage: 'No patients need review',
  },
  emptyBody: {
    id: 'ehrSync.review.emptyBody',
    defaultMessage:
      'Matches below the auto-link threshold appear here for a decision before any data syncs.',
  },
  loadFailed: {
    id: 'ehrSync.review.loadFailed',
    defaultMessage: "Couldn't load the review queue",
  },
  loading: {
    id: 'ehrSync.review.loading',
    defaultMessage: 'Loading the queue…',
  },
  engagePatient: {
    id: 'ehrSync.review.engagePatient',
    defaultMessage: 'Engage patient',
  },
  vitallId: { id: 'ehrSync.review.vitallId', defaultMessage: 'VITALL ID {id}' },
  enrolledBy: {
    id: 'ehrSync.review.enrolledBy',
    defaultMessage: 'enrolled by {name} {date}',
  },
  enrolledOn: {
    id: 'ehrSync.review.enrolledOn',
    defaultMessage: 'enrolled {date}',
  },
  bestScore: {
    id: 'ehrSync.review.bestScore',
    defaultMessage: 'Best score {score}',
  },
  belowThreshold: {
    id: 'ehrSync.review.belowThreshold',
    defaultMessage: 'below the auto-link threshold',
  },
  matchedAt: {
    id: 'ehrSync.review.matchedAt',
    defaultMessage: 'matched {date}',
  },
  rejectAll: {
    id: 'ehrSync.review.rejectAll',
    defaultMessage: '{count, plural, one {Reject} other {Reject all}}',
  },
  comparisonLabel: {
    id: 'ehrSync.review.comparisonLabel',
    defaultMessage: 'Engage patient compared with each {vendor} candidate',
  },
  field: { id: 'ehrSync.review.field', defaultMessage: 'Field' },
  engage: { id: 'ehrSync.review.engage', defaultMessage: 'Engage' },
  candidate: {
    id: 'ehrSync.review.candidate',
    defaultMessage: '{vendor} candidate {letter} · score',
  },
  linkTo: {
    id: 'ehrSync.review.linkTo',
    defaultMessage: 'Link to candidate {letter}',
  },
  linkNote: {
    id: 'ehrSync.review.linkNote',
    defaultMessage:
      'Linking sets the link to Linked with your name and the time. QIE starts syncing this patient on its next poll. The next case opens after you decide.',
  },
  linked: {
    id: 'ehrSync.review.linked',
    defaultMessage: '{name} linked to {externalId}',
  },
  rejected: {
    id: 'ehrSync.review.rejected',
    defaultMessage: 'Match rejected for {name}',
  },
  theEhr: { id: 'ehrSync.review.theEhr', defaultMessage: 'EHR' },
  missingPatient: {
    id: 'ehrSync.review.missingPatient',
    defaultMessage: 'Patient no longer in Engage',
  },
  female: { id: 'ehrSync.review.sex.female', defaultMessage: 'Female' },
  male: { id: 'ehrSync.review.sex.male', defaultMessage: 'Male' },
  other: { id: 'ehrSync.review.sex.other', defaultMessage: 'Other' },
  unknown: { id: 'ehrSync.review.sex.unknown', defaultMessage: 'Unknown' },
});

const fieldMessages = defineMessages<EhrMatchField>({
  family: { id: 'ehrSync.review.field.family', defaultMessage: 'Family name' },
  given: { id: 'ehrSync.review.field.given', defaultMessage: 'Given name' },
  birthDate: {
    id: 'ehrSync.review.field.birthDate',
    defaultMessage: 'Birth date',
  },
  gender: { id: 'ehrSync.review.field.gender', defaultMessage: 'Sex' },
  phone: { id: 'ehrSync.review.field.phone', defaultMessage: 'Phone' },
  address: { id: 'ehrSync.review.field.address', defaultMessage: 'Address' },
  postalCode: {
    id: 'ehrSync.review.field.postalCode',
    defaultMessage: 'Postal code',
  },
  email: { id: 'ehrSync.review.field.email', defaultMessage: 'Email' },
});

/** Row order of the comparison table, as in the design. */
const FIELD_ORDER: EhrMatchField[] = [
  'family',
  'given',
  'birthDate',
  'gender',
  'phone',
  'address',
  'postalCode',
  'email',
];

/** A, B, C… for candidate columns. */
const letter = (index: number) => String.fromCharCode(65 + index);

/** Props for {@link ReviewQueueTab}. */
interface ReviewQueueTabProps {
  cases: EhrReviewCase[];
  loading?: boolean;
  error?: ApolloError;
  onRetry?: () => void;
  /** EHR vendor for candidate headings, e.g. 'Epic'. */
  vendor?: string | null;
  /** The case to open first, e.g. from a Patients tab row. */
  initialLinkId?: string | null;
}

/**
 * Review queue tab.
 *
 * @param props - see {@link ReviewQueueTabProps}
 */
export default function ReviewQueueTab({
  cases,
  loading = false,
  error,
  onRetry,
  vendor,
  initialLinkId,
}: ReviewQueueTabProps) {
  const { formatMessage, formatNumber } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [review, reviewing] = useReviewEhrPatientLink();
  const [selectedId, setSelectedId] = useState<string | null>(
    initialLinkId ?? null
  );
  const [rejectOpen, setRejectOpen] = useState(false);
  // The case after the decided one opens next. Kept by id so a refetch that
  // adds or drops earlier cases cannot shift it; the position is the fallback
  // for when that case has left the queue too.
  const [nextId, setNextId] = useState<string | null>(null);
  const [lastIndex, setLastIndex] = useState(0);

  // A case chosen by id (from the Patients tab, a click, or pinned for the
  // reject dialog) that the queue does not have is reported, never swapped for
  // another patient's case. Only an unchosen view falls back to a position.
  const chosen =
    selectedId === null
      ? undefined
      : cases.find((c) => c.link.id === selectedId);
  const chosenMissing = selectedId !== null && !chosen;
  const atLastIndex: EhrReviewCase | null =
    cases[Math.min(lastIndex, cases.length - 1)] ?? null;
  const selected: EhrReviewCase | null =
    selectedId === null
      ? (cases.find((c) => c.link.id === nextId) ?? atLastIndex)
      : (chosen ?? null);
  const score = (value: number) =>
    formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const vendorName = vendor ?? formatMessage(messages.theEhr);

  const sexLabel = (value: string | null | undefined) => {
    switch (value) {
      case 'female':
        return formatMessage(messages.female);
      case 'male':
        return formatMessage(messages.male);
      case 'other':
        return formatMessage(messages.other);
      case 'unknown':
        return formatMessage(messages.unknown);
      default:
        return value ?? null;
    }
  };
  const display = (field: EhrMatchField, value: string | null | undefined) =>
    field === 'gender' ? sexLabel(value) : (value ?? null);

  const decide = async (
    decision: 'approve' | 'reject',
    externalPatientId?: string
  ) => {
    if (!selected) {
      return;
    }
    const index = cases.indexOf(selected);
    const name =
      patientName(selected.link.patient) ??
      formatMessage(messages.missingPatient);
    const decidedId = selected.link.id;
    const followingId = cases[index + 1]?.link.id ?? null;
    const saved = await review(decidedId, decision, externalPatientId);
    if (!saved) {
      // Usually a Conflict: someone else decided first. Reload so the case
      // leaves the queue instead of failing on every retry, and pin it so the
      // reload reports it gone rather than showing the next patient. A reviewer
      // who opened another case during the save stays on it.
      setRejectOpen(false);
      setSelectedId((current) =>
        current === null || current === decidedId ? decidedId : current
      );
      onRetry?.();
      return;
    }
    setRejectOpen(false);
    // Only move on if the reviewer has not picked another case meanwhile.
    setSelectedId((current) => (current === decidedId ? null : current));
    setNextId(followingId);
    setLastIndex(Math.max(index, 0));
    enqueueSnackbar({
      variant: 'success',
      message:
        decision === 'approve'
          ? formatMessage(messages.linked, {
              name,
              externalId: externalPatientId,
            })
          : formatMessage(messages.rejected, { name }),
    });
  };

  // cache-and-network keeps the loaded cases when a refetch fails, so only an
  // empty queue gets the full error panel. Otherwise the queue and the open
  // case stay put under a banner.
  if (error && cases.length === 0) {
    return (
      <StatePanel
        variant="error"
        title={formatMessage(messages.loadFailed)}
        body={error.message}
        onRetry={onRetry}
      />
    );
  }
  if (cases.length === 0) {
    return loading ? (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        {formatMessage(messages.loading)}
      </Typography>
    ) : (
      <StatePanel
        variant="empty"
        title={formatMessage(messages.empty)}
        body={formatMessage(messages.emptyBody)}
      />
    );
  }

  const name = selected
    ? (patientName(selected.link.patient) ??
      formatMessage(messages.missingPatient))
    : '';

  return (
    <Stack spacing={2.25}>
      {error ? (
        <Alert
          severity="error"
          action={
            onRetry ? (
              <Button color="inherit" size="small" onClick={onRetry}>
                {formatMessage(commonMessages.retry)}
              </Button>
            ) : undefined
          }
        >
          <AlertTitle>{formatMessage(messages.loadFailed)}</AlertTitle>
          {error.message}
        </Alert>
      ) : null}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.25}>
        <Paper variant="outlined" sx={{ width: { md: 280 }, flexShrink: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Typography variant="subtitle2" fontWeight="bold">
              {formatMessage(messages.queueTitle)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatMessage(messages.oldestFirst)}
            </Typography>
          </Stack>
          <List disablePadding aria-label={formatMessage(messages.queueTitle)}>
            {cases.map((c) => (
              <ListItem key={c.link.id} disablePadding>
                <ListItemButton
                  selected={c === selected}
                  aria-current={c === selected ? 'true' : undefined}
                  onClick={() => {
                    setRejectOpen(false);
                    setSelectedId(c.link.id);
                  }}
                  sx={{
                    display: 'block',
                    width: '100%',
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" fontWeight="bold">
                      {patientName(c.link.patient) ??
                        formatMessage(messages.missingPatient)}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {c.bestScore === null ? '-' : score(c.bestScore)}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="p"
                  >
                    {formatMessage(messages.queueItemMeta, {
                      dob: c.link.patient?.birthDate
                        ? formatMessage(messages.dob, {
                            date: c.link.patient.birthDate,
                          })
                        : formatMessage(messages.noDob),
                      count: c.candidates.length,
                    })}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="p"
                  >
                    {formatMessage(messages.waiting, {
                      when: <RelativeTime iso={c.matchedAt} />,
                    })}
                  </Typography>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>

        {chosenMissing ? (
          <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 2 }}>
            {loading ? (
              <Typography variant="body2" color="text.secondary">
                {formatMessage(messages.loading)}
              </Typography>
            ) : (
              <Alert severity="info">
                {formatMessage(messages.notInQueue)}
              </Alert>
            )}
          </Paper>
        ) : null}
        {selected ? (
          <Paper variant="outlined" sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" component="h2" fontWeight="bold">
                  {name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {[
                    formatMessage(messages.engagePatient),
                    selected.link.patient?.vitallUserId !== null &&
                    selected.link.patient?.vitallUserId !== undefined
                      ? formatMessage(messages.vitallId, {
                          id: formatClientId(
                            selected.link.patient.vitallUserId,
                            false
                          ),
                        })
                      : null,
                    selected.link.enrolledByName
                      ? formatMessage(messages.enrolledBy, {
                          name: selected.link.enrolledByName,
                          date: formatDate({
                            date: selected.link.createdAt,
                            formatter: 'date',
                          }),
                        })
                      : formatMessage(messages.enrolledOn, {
                          date: formatDate({
                            date: selected.link.createdAt,
                            formatter: 'date',
                          }),
                        }),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[
                    selected.bestScore === null
                      ? null
                      : formatMessage(messages.bestScore, {
                          score: score(selected.bestScore),
                        }),
                    selected.bestScore !== null &&
                    selected.bestScore < selected.autoLinkThreshold
                      ? formatMessage(messages.belowThreshold)
                      : null,
                    selected.algorithm,
                    formatMessage(messages.matchedAt, {
                      date: formatDate({
                        date: selected.matchedAt,
                        formatter: 'datetime',
                      }),
                    }),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>
              <Box>
                <Button
                  color="error"
                  variant="outlined"
                  disabled={reviewing}
                  onClick={() => {
                    // Pin the case so a refetch cannot swap the patient under the dialog.
                    setSelectedId(selected.link.id);
                    setRejectOpen(true);
                  }}
                >
                  {formatMessage(messages.rejectAll, {
                    count: selected.candidates.length,
                  })}
                </Button>
              </Box>
            </Stack>

            <TableContainer>
              <Table
                size="small"
                aria-label={formatMessage(messages.comparisonLabel, {
                  vendor: vendorName,
                })}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography
                        variant="overline"
                        fontWeight="bold"
                        color="text.secondary"
                      >
                        {formatMessage(messages.field)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="overline"
                        fontWeight="bold"
                        color="text.secondary"
                      >
                        {formatMessage(messages.engage)}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {name}
                      </Typography>
                    </TableCell>
                    {selected.candidates.map((candidate, index) => (
                      <TableCell key={candidate.externalPatientId}>
                        <Typography
                          variant="overline"
                          fontWeight="bold"
                          color="text.secondary"
                        >
                          {formatMessage(messages.candidate, {
                            vendor: vendorName,
                            letter: letter(index),
                          })}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {score(candidate.score)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {candidate.externalPatientId}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {FIELD_ORDER.filter(
                    // Absent everywhere means QIE did not compare it; a row of
                    // Missing markers would read as evidence.
                    (field) =>
                      field in selected.subject ||
                      selected.candidates.some((c) => field in c.fields)
                  ).map((field) => (
                    <TableRow key={field}>
                      <TableCell component="th" scope="row">
                        <Typography
                          variant="overline"
                          fontWeight="bold"
                          color="text.secondary"
                        >
                          {formatMessage(fieldMessages[field])}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-line' }}
                        >
                          {display(field, selected.subject[field]) ?? '-'}
                        </Typography>
                      </TableCell>
                      {selected.candidates.map((candidate) => {
                        const evidence = candidate.fields[field];
                        return (
                          <TableCell key={candidate.externalPatientId}>
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: 'pre-line' }}
                            >
                              {display(field, evidence?.value) ?? '-'}
                            </Typography>
                            <EvidenceMarker
                              result={evidence?.result ?? 'missing'}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack
              spacing={1.5}
              sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}
            >
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {selected.candidates.map((candidate, index) => (
                  <Button
                    key={candidate.externalPatientId}
                    variant="contained"
                    disabled={reviewing}
                    onClick={() => {
                      void decide('approve', candidate.externalPatientId);
                    }}
                  >
                    {formatMessage(messages.linkTo, { letter: letter(index) })}
                  </Button>
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {formatMessage(messages.linkNote)}
              </Typography>
            </Stack>

            <RejectMatchDialog
              open={rejectOpen}
              patientName={name}
              candidateCount={selected.candidates.length}
              vendor={vendor}
              loading={reviewing}
              onCancel={() => setRejectOpen(false)}
              onConfirm={() => {
                void decide('reject');
              }}
            />
          </Paper>
        ) : null}
      </Stack>
    </Stack>
  );
}
